"use client";

import { useMemo } from "react";

import Image from "next/image";
import { useServiceContent } from "@/hooks/use-service-content";
import { Section } from "@/components/section";
import { Card, CardContent } from "@/components/ui/card";
import { Text } from "@/components/ui/text";
import { SeoHeading } from "@/components/common/seo-heading";
import { RenderIf } from "@/components/common/render-if";
import { cn } from "@/lib/utils";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { hasData } from "@/helpers/has-data";

type ExamFormatProps = {
  section: PageSections;
  data: {
    examFormat: ServiceContent;
    image?: string;
    isMappedServices: boolean;
  };
};

const ExamFormat = ({ data, section }: ExamFormatProps) => {
  const { heading, seoHeading, image, list, description } = useServiceContent(
    data.examFormat,
    true
  );

  const imagePath = useMemo(() => {
    if (image) {
      return image;
    }
    if (data.image) {
      return data.image;
    }
    return undefined;
  }, [image, data.image]);
  if (!hasData(data)) return null;
  return (
    <Section className={section.sectionClass} id={section.sectionId} data-title={section.name}>
      <Card>
        <CardContent className="grid grid-cols-1 gap-5 sm:grid-cols-[2fr_1fr]">
          <div>
            <SeoHeading heading={heading} seoHeading={seoHeading} as="h2" />
            <RenderIf condition={Boolean(description)}>
              <Text
                className={cn(
                  "mt-6",
                  "[&_ul]:list-none [&_ul]:space-y-2",
                  "[&_li]:relative [&_li]:ml-7",
                  "[&_li]:before:content-['']",
                  "[&_li]:before:absolute",
                  "[&_li]:before:-left-7.5",
                  "[&_li]:before:top-1/2",
                  "[&_li]:before:-translate-y-1/2",
                  "[&_li]:before:size-5",
                  "[&_li]:before:bg-primary",
                  "[&_li]:before:[mask:url('/images/check-circle.svg')]",
                  "[&_li]:before:mask-no-repeat",
                  "[&_li]:before:mask-center",
                  "[&_li]:before:mask-contain",
                  "[&_li]:before:[-webkit-mask:url('/images/check-circle.svg')]",
                  "[&_li]:before:[-webkit-mask-repeat:no-repeat]",
                  "[&_li]:before:[-webkit-mask-position:center]",
                  "[&_li]:before:[-webkit-mask-size:contain]",
                  {
                    "sm:grid-cols-2": data.isMappedServices,
                    "sm:grid-cols-1": !data.isMappedServices,
                  }
                )}
              >
                {description}
              </Text>
            </RenderIf>
          </div>

          <RenderIf condition={Boolean(imagePath) && Boolean(list)}>
            <AspectRatio ratio={400 / 283} className="w-full">
              <Image
                src={imagePath!}
                alt={heading}
                fill
                sizes="400px"
                className="w-full object-center"
              />
            </AspectRatio>
          </RenderIf>
        </CardContent>
      </Card>
    </Section>
  );
};

export default ExamFormat;
